import { useState, type SyntheticEvent } from 'react';
import { API_PREFIX } from '../api/client.js';
import type { ApiScope, ApiToken, CreatedApiToken, Point, Site } from '../api/types.js';
import { useApi, useAuth } from '../auth.js';
import { CopyBlock, ErrorBox, Spinner } from '../components.js';
import { useNow, useResource } from '../hooks.js';
import { formatAge, formatTimestamp } from '../lib/format.js';
import {
  DEFAULT_EXPIRY_DAYS,
  EXPIRY_OPTIONS,
  mcpUrl,
  pointAccessBody,
  SCOPE_INFO,
  tokenPoints,
  tokenSites,
  tokenSnippets,
  tokenState,
  type PointMode,
  type TokenState,
} from '../lib/tokens.js';
import { TokenPointsField } from '../TokenPoints.js';

const STATE_LABEL: Record<TokenState, string> = {
  active: 'Active',
  expired: 'Expired',
  revoked: 'Revoked',
};
const STATE_CLASS: Record<TokenState, string> = {
  active: 'badge-config-applied',
  expired: 'badge-unknown',
  revoked: 'badge-config-rejected',
};

/** Shown once after a token was created: the secret and how to use it. */
function CreatedPanel({ created, onClose }: { created: CreatedApiToken; onClose: () => void }) {
  const urls = {
    api: window.location.origin,
    mcp: mcpUrl(window.location, import.meta.env.VITE_MCP_URL),
  };
  const snippets = tokenSnippets(created.token, urls);
  return (
    <div className="card token-created" role="status">
      <div className="section-head">
        <h2>Token “{created.name}” created</h2>
        <button type="button" className="btn btn-small" onClick={onClose}>
          Done
        </button>
      </div>
      <p className="warn strong">
        Copy the token now. You will not see it again; if you lose it, revoke it and create a new
        one.
      </p>
      <CopyBlock label="Token" text={created.token} />
      <CopyBlock label="Claude Code (MCP server over HTTP)" text={snippets.claudeCode} />
      <CopyBlock label="API with curl" text={snippets.curl} />
      <p className="muted small">
        MCP endpoint: <span className="mono">{urls.mcp}</span>. All endpoints:{' '}
        <a href={`${API_PREFIX}/docs`} target="_blank" rel="noreferrer">
          API browser
        </a>
        .
      </p>
    </div>
  );
}

function CreateForm({
  sites,
  onCreated,
}: {
  sites: readonly Site[] | undefined;
  onCreated: (t: CreatedApiToken) => void;
}) {
  const api = useApi();
  const platform = useAuth().user?.permissions.platform ?? false;
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ReadonlySet<ApiScope>>(new Set(['read']));
  const [siteIds, setSiteIds] = useState<ReadonlySet<string>>(new Set());
  const [days, setDays] = useState<number | null>(DEFAULT_EXPIRY_DAYS);
  const [pointMode, setPointMode] = useState<PointMode>('none');
  const [picked, setPicked] = useState<ReadonlyMap<string, Point>>(new Map());
  const [tags, setTags] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  function toggle(scope: ApiScope, on: boolean) {
    const next = new Set(scopes);
    if (on) next.add(scope);
    else next.delete(scope);
    setScopes(next);
  }

  function toggleSite(id: string, on: boolean) {
    const next = new Set(siteIds);
    if (on) next.add(id);
    else next.delete(id);
    setSiteIds(next);
  }

  const restrictedAdmin = siteIds.size > 0 && scopes.has('admin');
  // Picked points outside the token's sites would be refused: only those inside count.
  const pickedIds = [...picked.values()]
    .filter((p) => siteIds.size === 0 || siteIds.has(p.site.id))
    .map((p) => p.id);
  const points = pointAccessBody(pointMode, pickedIds, tags, scopes);
  const pickerSites = sites?.filter((x) => siteIds.size === 0 || siteIds.has(x.id));

  async function submit(e: SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await api.createApiToken({
        name: name.trim(),
        scopes: SCOPE_INFO.map((s) => s.scope).filter((s) => scopes.has(s)),
        ...(days !== null && { expires_in_days: days }),
        ...(siteIds.size > 0 && {
          site_ids: (sites ?? []).map((x) => x.id).filter((id) => siteIds.has(id)),
        }),
        ...(points.ok && points.points && { points: points.points }),
      });
      setName('');
      setScopes(new Set(['read']));
      setSiteIds(new Set());
      setDays(DEFAULT_EXPIRY_DAYS);
      setPointMode('none');
      setPicked(new Map());
      setTags([]);
      onCreated(created);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="card token-form"
      onSubmit={(e) => {
        void submit(e);
      }}
    >
      <label className="field">
        <span>Name</span>
        <input
          type="text"
          maxLength={200}
          placeholder="e.g. Claude on my laptop"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
      </label>
      <fieldset className="token-scopes">
        <legend className="strong">Scopes</legend>
        {SCOPE_INFO.map((s) => (
          <label key={s.scope} className="token-scope">
            <input
              type="checkbox"
              checked={scopes.has(s.scope)}
              onChange={(e) => {
                toggle(s.scope, e.target.checked);
              }}
            />
            <span>
              <span className="mono strong">{s.label}</span>
              <span className="muted small"> — {s.help}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <fieldset className="token-scopes">
        <legend className="strong">Sites</legend>
        <p className="muted small">
          {platform
            ? 'None ticked = a platform token (every tenant). Ticked sites bind the token to their tenant.'
            : 'None ticked = all sites of your tenant. Your tokens can only read points, their current values and history.'}{' '}
          {platform &&
            'A token restricted to sites or points (a customer token) can only read points, their current values and history.'}
        </p>
        {!sites ? (
          <Spinner />
        ) : (
          sites.map((x) => (
            <label key={x.id} className="token-scope">
              <input
                type="checkbox"
                checked={siteIds.has(x.id)}
                onChange={(e) => {
                  toggleSite(x.id, e.target.checked);
                }}
              />
              <span>{x.name}</span>
            </label>
          ))
        )}
        {restrictedAdmin && (
          <p className="form-error">A token restricted to sites cannot have the admin scope.</p>
        )}
      </fieldset>
      <TokenPointsField
        mode={pointMode}
        onMode={setPointMode}
        sites={pickerSites}
        anySite={siteIds.size === 0}
        picked={picked}
        onPicked={setPicked}
        tags={tags}
        onTags={setTags}
        problem={pointMode !== 'none' && !points.ok ? points.problem : null}
      />
      <div className="token-expiry">
        <span className="strong">Expires after</span>
        <div className="segmented" role="group" aria-label="Expires after">
          {EXPIRY_OPTIONS.map((o) => (
            <button
              key={o.label}
              type="button"
              className={o.days === days ? 'active' : undefined}
              aria-pressed={o.days === days}
              onClick={() => {
                setDays(o.days);
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
      {error && <ErrorBox error={error} />}
      <div>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={busy || !name.trim() || scopes.size === 0 || restrictedAdmin || !points.ok}
        >
          {busy ? 'Creating…' : 'Create token'}
        </button>
      </div>
    </form>
  );
}

/** "3 d ago" with the full time on hover; "never" for null. */
function When({ ts, now }: { ts: number | null; now: number }) {
  if (ts === null) return <span className="muted">never</span>;
  return (
    <time dateTime={new Date(ts).toISOString()} title={formatTimestamp(ts)}>
      {formatAge(ts, now)}
    </time>
  );
}

const day = (ts: number) => formatTimestamp(ts).slice(0, 10);

function TokenRow({
  t,
  sites,
  now,
  busy,
  onRevoke,
}: {
  t: ApiToken;
  sites: readonly Site[] | undefined;
  now: number;
  busy: boolean;
  onRevoke: (t: ApiToken) => void;
}) {
  const state = tokenState(t, now);
  return (
    <tr className={state === 'active' ? undefined : 'muted'}>
      <td className="strong">{t.name}</td>
      <td className="mono small">{t.scopes.join(', ')}</td>
      <td className="small">{t.platform ? 'Platform: all tenants' : tokenSites(t, sites)}</td>
      <td className="small">{t.platform ? '' : tokenPoints(t)}</td>
      <td>
        <When ts={t.created_at} now={now} />
      </td>
      <td>{t.expires_at === null ? 'never' : day(t.expires_at)}</td>
      <td>
        <When ts={t.last_used_at} now={now} />
      </td>
      <td>
        <span className={`badge ${STATE_CLASS[state]}`}>{STATE_LABEL[state]}</span>
        {t.revoked_at !== null && <div className="muted small">{day(t.revoked_at)}</div>}
      </td>
      <td>
        {state === 'active' && (
          <button
            type="button"
            className="btn btn-small"
            disabled={busy}
            onClick={() => {
              onRevoke(t);
            }}
          >
            Revoke
          </button>
        )}
      </td>
    </tr>
  );
}

/**
 * API tokens (§7 "Public API"): `Authorization: Bearer kal_…` for scripts and
 * the MCP server. The secret is shown once, right after creation.
 */
export function ApiTokensPage() {
  const api = useApi();
  const now = useNow(30_000);
  const tokens = useResource('api-tokens', (s) => api.apiTokens(s));
  const sites = useResource('sites', () => api.sites());
  const [created, setCreated] = useState<CreatedApiToken | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [actionError, setActionError] = useState<Error | null>(null);

  async function revoke(t: ApiToken) {
    if (!window.confirm(`Revoke the token “${t.name}”? Clients using it stop working at once.`)) {
      return;
    }
    setRevoking(t.id);
    setActionError(null);
    try {
      await api.revokeApiToken(t.id);
      if (created?.id === t.id) setCreated(null);
      tokens.reload();
    } catch (err) {
      setActionError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setRevoking(null);
    }
  }

  return (
    <section>
      <div className="breadcrumbs">Settings /</div>
      <h1>API tokens</h1>
      <p className="muted">
        Tokens let scripts and AI assistants (the Kalvio MCP server) use the API without a login:
        send <span className="mono">Authorization: Bearer kal_…</span>. Use{' '}
        <span className="mono">read</span> unless a client must change something, and restrict
        tokens for customers to their sites or points: such tokens can only read points, their
        current values and history.{' '}
        <a href={`${API_PREFIX}/docs`} target="_blank" rel="noreferrer">
          API browser
        </a>
      </p>
      {created && (
        <CreatedPanel
          created={created}
          onClose={() => {
            setCreated(null);
          }}
        />
      )}
      <div className="section-head">
        <h2>New token</h2>
      </div>
      <CreateForm
        sites={sites.data}
        onCreated={(t) => {
          setCreated(t);
          tokens.reload();
        }}
      />
      <div className="section-head">
        <h2>Tokens</h2>
      </div>
      {tokens.error && <ErrorBox error={tokens.error} onRetry={tokens.reload} />}
      {actionError && <ErrorBox error={actionError} />}
      {!tokens.data ? (
        !tokens.error && <Spinner />
      ) : tokens.data.length === 0 ? (
        <p className="muted">No tokens yet.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Scopes</th>
                <th>Sites</th>
                <th>Points</th>
                <th>Created</th>
                <th>Expires</th>
                <th>Last used</th>
                <th>State</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {tokens.data.map((t) => (
                <TokenRow
                  key={t.id}
                  t={t}
                  sites={sites.data}
                  now={now}
                  busy={revoking === t.id}
                  onRevoke={(x) => {
                    void revoke(x);
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
